import { useId, useMemo, useRef, useState } from 'react';

import { Link } from 'react-router-dom';

import { Button, Checkbox, Dialog, Field, Input, Select } from '../../../components/ui';
import { useDuongDan } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { loiTheoTruong, maLoi } from '../../../lib/errors';
import { useChinhSachEmailToChuc } from '../../bao-mat/hooks/useBaoMat';
import { ChonPhongBan } from '../../co-cau-to-chuc/components/ChonPhongBan';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { useDanhSachVaiTro } from '../../vai-tro/hooks/useVaiTro';
import { KHOA_LOI_TEN } from '../constants';
import { DAI_TOI_DA, kiemEmail, kiemEmailTuyChon, kiemTen } from '../nghiep-vu/thao-tac';
import type { LoiEmail } from '../nghiep-vu/thao-tac';
import type { UserInvite } from '../types';

const KHOA_LOI_EMAIL: Record<LoiEmail, KhoaDich> = {
  trong: 'nguoiDung.fEmailTrong',
  dinhDang: 'nguoiDung.fEmailSai',
  dai: 'nguoiDung.fQuaDai',
};

/** Lỗi nghiệp vụ nói về chính địa chỉ email: đặt dưới ô email, không gom vào dải chung. */
const LOI_CUA_EMAIL = new Set(['USER_ALREADY_EXISTS', 'EMAIL_DOMAIN_NOT_IN_TENANT', 'EMAIL_NOT_INVITABLE']);

/** Phòng ban vừa bị xoá ở phiên khác (`404`, không mang `pointer`): đặt dưới ô phòng ban. */
const LOI_CUA_PHONG_BAN = 'DEPARTMENT_NOT_FOUND';

const O_CO_TREN_FORM = /^\/(email|email_nhan_thu|display_name|platform_role|department_id)$/;

/**
 * Hộp thoại mời người dùng (`POST /api/v1/users/invite`).
 *
 * `Idempotency-Key` sinh mới mỗi lần mở hộp (form dựng lại theo `key`), giữ
 * nguyên khi gửi lại đúng nội dung — bấm hai lần hay mất mạng không mời hai
 * lần. Nội dung đổi sau một lần lỗi thì khoá mới, vì dùng lại khoá cho nội
 * dung khác là `422 IDEMPOTENCY_KEY_REUSED`.
 *
 * Phòng ban tuỳ chọn (`department_id`, ADR-0014): bỏ trống thì không gửi.
 *
 * Hai lớp vai trò không cùng một ô (ADR-0004): ô tích "Quản trị tổ chức" là
 * role lớp 1 (`platform_role`); khi không tích, ô "Vai trò" chọn vai trò lớp 2
 * của tổ chức — cùng danh sách màn Cấu hình vai trò — để gán ngay sau lời mời.
 * Vai trò lớp 2 không nằm trong `Idempotency-Key`: API mời không nhận chúng.
 */
export function FormMoiNguoiDung({
  open,
  dangGui,
  onClose,
  onMoi,
}: {
  open: boolean;
  dangGui: boolean;
  onClose: () => void;
  /** `roleIds`: vai trò lớp 2 gán ngay sau lời mời; rỗng khi mời Quản trị tổ chức. */
  onMoi: (du: UserInvite, khoa: string, roleIds: string[]) => Promise<unknown>;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const formId = useId();
  const chinhSachEmail = useChinhSachEmailToChuc();
  const lanGui = useRef<{ noiDung: string; khoa: string } | null>(null);

  const [email, setEmail] = useState('');
  // ADR-0101: hộp thư nhận thư mời và thông báo nếu khác email đăng nhập; trống là trùng.
  const [nhanThu, setNhanThu] = useState('');
  const [ten, setTen] = useState('');
  const [laQuanTri, setLaQuanTri] = useState(false);
  const [vaiTro, setVaiTro] = useState<string[]>([]);
  const [phongBan, setPhongBan] = useState<string | null>(null);
  const [daGui, setDaGui] = useState(false);
  const [loiServer, setLoiServer] = useState<{ loi: unknown; truong: Record<string, string> } | null>(
    null,
  );

  const boLoiServer = (duongDan: string) =>
    setLoiServer((cu) => {
      if (!cu || !(duongDan in cu.truong)) return cu;
      const { [duongDan]: _boQua, ...conLai } = cu.truong;
      // Lỗi nghiệp vụ của ô email đã rời ô đó thì không còn gì để nói trong dải chung.
      return Object.keys(conLai).length === 0 && LOI_CUA_EMAIL.has(maLoi(cu.loi) ?? '')
        ? null
        : { ...cu, truong: conLai };
    });

  const maEmail = kiemEmail(email);
  const maNhanThu = kiemEmailTuyChon(nhanThu);
  const loiNhanThu =
    (maNhanThu && (daGui || maNhanThu === 'dai')
      ? t(KHOA_LOI_EMAIL[maNhanThu], { so: DAI_TOI_DA.email })
      : undefined) ?? loiServer?.truong['/email_nhan_thu'];
  const maTen = kiemTen(ten);
  // Thiếu thì chỉ báo sau lần bấm gửi đầu; quá dài thì báo ngay khi gõ.
  const loiEmail =
    (maEmail && (daGui || maEmail === 'dai')
      ? t(KHOA_LOI_EMAIL[maEmail], { so: DAI_TOI_DA.email })
      : undefined) ?? loiServer?.truong['/email'];
  const loiTen =
    (maTen && (daGui || maTen === 'dai')
      ? t(KHOA_LOI_TEN[maTen], { so: DAI_TOI_DA.display_name })
      : undefined) ?? loiServer?.truong['/display_name'];

  const loiVaiTroLop1 = loiServer?.truong['/platform_role'];

  /*
   * ADR-0099: tổ chức cho phép email ngoài domain đã xác minh thì mời được email cá nhân, nhưng gõ nhầm một chữ là
   * mời người lạ vào tổ chức — nhắc nhẹ khi email hợp lệ mà không thuộc domain nào của tổ chức. Không chặn gửi.
   */
  const domainEmail = email.trim().toLowerCase().split('@')[1] ?? '';
  const nhacNgoaiDomain =
    chinhSachEmail.emailNgoaiDomain === true &&
    !maEmail &&
    !loiEmail &&
    chinhSachEmail.domains !== undefined &&
    !chinhSachEmail.domains.includes(domainEmail);

  const maLoiServer = maLoi(loiServer?.loi) ?? '';
  const loiChung =
    loiServer && !LOI_CUA_EMAIL.has(maLoiServer) && maLoiServer !== LOI_CUA_PHONG_BAN ? loiServer : null;
  const loiKhongCoO = Object.entries(loiServer?.truong ?? {}).filter(
    ([duongDan]) => !O_CO_TREN_FORM.test(duongDan),
  );

  const gui = async () => {
    setDaGui(true);
    if (maEmail || maNhanThu || maTen || dangGui) return;
    const du: UserInvite = {
      email: email.trim(),
      ...(nhanThu.trim() ? { email_nhan_thu: nhanThu.trim() } : {}),
      display_name: ten.trim(),
      platform_role: laQuanTri ? 'TENANT_ADMIN' : 'TENANT_USER',
      ...(phongBan ? { department_id: phongBan } : {}),
    };
    const noiDung = JSON.stringify(du);
    const khoa =
      lanGui.current?.noiDung === noiDung ? lanGui.current.khoa : crypto.randomUUID();
    lanGui.current = { noiDung, khoa };
    setLoiServer(null);
    try {
      await onMoi(du, khoa, laQuanTri ? [] : vaiTro);
    } catch (err) {
      const truong = loiTheoTruong(err);
      // Lỗi nghiệp vụ của email không mang `pointer`: tự gắn vào ô email.
      if (LOI_CUA_EMAIL.has(maLoi(err) ?? '')) truong['/email'] = dichLoi(err);
      if (maLoi(err) === LOI_CUA_PHONG_BAN) truong['/department_id'] = dichLoi(err);
      setLoiServer({ loi: err, truong });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('nguoiDung.moiTieuDe')}
      width={520}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {(loiChung || loiKhongCoO.length > 0) && (
            <LoiTrongHop>
              {loiChung && dichLoi(loiChung.loi)}
              {loiKhongCoO.map(([duongDan, thongDiep]) => (
                <span key={duongDan} className="mt-1 block">
                  <span className="font-mono">{duongDan}</span>: {thongDiep}
                </span>
              ))}
            </LoiTrongHop>
          )}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={dangGui}>
              {dangGui ? t('nguoiDung.dangGuiLoiMoi') : t('nguoiDung.guiLoiMoi')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          void gui();
        }}
      >
        <div className="space-y-1.5">
          <Field
            label={t('nguoiDung.fEmail')}
            required
            // Chỉ nói ràng buộc domain khi chắc chắn có; đang tải hay nhận cả email cá nhân thì không cần gợi ý
            hint={chinhSachEmail.emailNgoaiDomain === false ? t('nguoiDung.fEmailGoiY') : undefined}
            error={loiEmail}
          >
            {(p) => (
              <Input
                {...p}
                aria-describedby={
                  nhacNgoaiDomain ? `${p['aria-describedby'] ?? ''} ${formId}-ngoai-domain`.trim() : p['aria-describedby']
                }
                autoFocus
                type="email"
                inputMode="email"
                autoComplete="off"
                spellCheck={false}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  boLoiServer('/email');
                }}
              />
            )}
          </Field>
          {nhacNgoaiDomain && (
            <p
              id={`${formId}-ngoai-domain`}
              role="status"
              className="rounded border border-warning-200 bg-warning-50 px-3 py-2 text-xs leading-relaxed text-warning-700"
            >
              {t('nguoiDung.nhacNgoaiDomain', { domain: domainEmail })}
            </p>
          )}
        </div>

        <Field label={t('nguoiDung.fEmailNhanThu')} error={loiNhanThu}>
          {(p) => (
            <Input
              {...p}
              type="email"
              inputMode="email"
              autoComplete="off"
              spellCheck={false}
              value={nhanThu}
              onChange={(e) => {
                setNhanThu(e.target.value);
                boLoiServer('/email_nhan_thu');
              }}
            />
          )}
        </Field>

        <Field label={t('nguoiDung.fTenHienThi')} required error={loiTen}>
          {(p) => (
            <Input
              {...p}
              value={ten}
              onChange={(e) => {
                setTen(e.target.value);
                boLoiServer('/display_name');
              }}
            />
          )}
        </Field>

        <div className="space-y-1.5">
          <Checkbox
            checked={laQuanTri}
            aria-describedby={loiVaiTroLop1 ? `${formId}-qt-err` : undefined}
            onChange={(e) => {
              setLaQuanTri(e.target.checked);
              // Quản trị đã toàn quyền: bỏ vai trò đã chọn, khỏi gửi nhầm khi bỏ tích lại.
              if (e.target.checked) setVaiTro([]);
              boLoiServer('/platform_role');
            }}
          >
            <span className="text-sm text-content-primary">{t('vaiTro.quanTriToChuc')}</span>
            <span className="block text-xs text-content-muted">{t('nguoiDung.fQuanTriMo')}</span>
            {laQuanTri && (
              <span className="mt-1 block text-xs text-content-muted">{t('nguoiDung.fQuanTriLuuY')}</span>
            )}
          </Checkbox>
          {loiVaiTroLop1 && (
            <p id={`${formId}-qt-err`} role="alert" className="text-xs text-danger-600">
              {loiVaiTroLop1}
            </p>
          )}
        </div>

        {!laQuanTri && <ChonVaiTroLop2 value={vaiTro} onChange={setVaiTro} />}

        <ChonPhongBan
          value={phongBan}
          error={loiServer?.truong['/department_id']}
          onChange={(id) => {
            setPhongBan(id);
            boLoiServer('/department_id');
          }}
        />
      </form>
    </Dialog>
  );
}

/**
 * Ô chọn vai trò lớp 2 của hộp mời — tuỳ chọn, chọn nhiều, tìm theo tên. Dùng
 * chung hook danh sách với màn Cấu hình vai trò. Tổ chức chưa có vai trò nào
 * thì thay ô bằng lời nhắc kèm đường tới Cấu hình vai trò; không tải được thì
 * báo cạnh ô, vẫn mời được (gán sau).
 */
function ChonVaiTroLop2({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const { t } = useT();
  const duongDan = useDuongDan();
  const ds = useDanhSachVaiTro();
  const options = useMemo(
    () => (ds.data ?? []).map((r) => ({ value: r.id, label: r.name })),
    [ds.data],
  );

  if (ds.data && ds.data.length === 0) {
    return (
      <div className="space-y-1.5">
        <span className="block text-sm font-medium text-content-secondary">{t('nguoiDung.fVaiTro')}</span>
        <p className="rounded border border-dashed border-default px-3 py-2.5 text-xs leading-relaxed text-content-muted">
          {t('nguoiDung.fVaiTroRong')}{' '}
          <Link to={duongDan('/he-thong/vai-tro')} className="font-medium text-primary-700 hover:underline">
            {t('vaiTroLop2.denCauHinhVaiTro')}
          </Link>
        </p>
      </div>
    );
  }

  return (
    <Field
      label={t('nguoiDung.fVaiTro')}
      error={ds.error && !ds.data ? t('nguoiDung.fVaiTroLoiTai') : undefined}
    >
      {(p) => (
        <Select<string[]>
          {...p}
          mode="multiple"
          allowClear
          className="w-full"
          showSearch={{ optionFilterProp: 'label' }}
          placeholder={t('nguoiDung.fVaiTroChon')}
          notFoundContent={t('nguoiDung.fVaiTroKhongKhop')}
          loading={ds.isLoading}
          status={ds.error && !ds.data ? 'error' : undefined}
          options={options}
          value={value}
          onChange={onChange}
          // Mở lại ô sau một lần tải hỏng thì thử lại.
          onOpenChange={(mo) => {
            if (mo && ds.error && !ds.data) void ds.refetch();
          }}
        />
      )}
    </Field>
  );
}
